import { useApi, apiPost } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtNum, VENDOR_LABEL } from "@/lib/format";
import { toast } from "sonner";
import { AppWindow } from "lucide-react";

const BEST_FOR = {
  "M365 Copilot": "Documents, email, meetings inside Microsoft 365",
  "Claude Enterprise": "Long documents, contracts, projects with knowledge",
  "Claude Code": "Coding, refactoring private repositories",
  "OpenAI GPT API": "Custom apps and agents via the Navitra Gate",
  "ChatGPT Enterprise": "General drafting, research with connectors",
  "Copilot Studio": "Building internal agents and workflows",
};

export default function MyTools() {
  const { data: apps = [], isLoading, error } = useApi("apps", "/apps");
  const request = async (a) => { await apiPost("/requests", { tool: a.name, reason: "Requested from My AI Tools" }); toast.success(`Access requested for ${a.name}`); };
  if (error) return <ErrorState />;
  const sanctioned = apps.filter((a) => a.status === "sanctioned");
  const others = apps.filter((a) => a.status !== "sanctioned");

  return (
    <div>
      <PageHeader eyebrow="Navitra Companion" title="My AI Tools" subtitle="Approved tools, what each is best for, the data allowed — and how to request more." />
      {isLoading ? <LoadingBlock /> : (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-lg mb-3">Approved for you</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sanctioned.map((a) => (
                <Card key={a.id} data-testid={`tool-${a.id}`}>
                  <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base flex items-center gap-2"><AppWindow className="h-4 w-4 text-navy" />{a.name}</CardTitle><SeverityChip level="sanctioned" /></div></CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p className="text-muted-foreground">{BEST_FOR[a.name] || a.notes}</p>
                    <p className="text-xs text-muted-foreground">Vendor: {VENDOR_LABEL[a.vendor]}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-lg mb-3">Other discovered tools</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((a) => (
                <Card key={a.id} className="opacity-90">
                  <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base">{a.name}</CardTitle><SeverityChip level={a.status} /></div></CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p className="text-muted-foreground">{a.notes}</p>
                    {a.status === "tolerated" ? <Button size="sm" variant="outline" onClick={() => request(a)} data-testid={`request-${a.id}`}>Request access</Button> : <p className="text-xs text-sev-danger">Blocked on managed devices</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
